import React, { useState, useEffect } from "react";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { TextField } from "../../../../ui/components/TextField";
import { FeatherEdit } from "@subframe/core";
import { FeatherSave } from "@subframe/core";
import { FeatherX } from "@subframe/core";
import { FeatherChevronDown } from "@subframe/core";
import ReviewChangesModal, { ChangeItem } from "./ReviewChangesModal";
import { fetchCurrencies, Currency } from "../../../../services/api";
import { handleError } from "../../../../utils/errorHandling";
import { SearchableDropdown } from "../../components";
import { ReviewPendingInlineValue } from "./ReviewFieldDiffHint";
import { getDocumentNumberValidationWarning } from "../../../utils/documentNumberValidation";
import {
  getExchangeRateDisplayText,
  isImplicitDefaultExchangeRate,
} from "../../../utils/documentExchangeRate";

export type InvoiceBasicFieldDiffSlice = {
  oldText: string;
  newText: string;
};

interface BasicInfoData {
  invoiceType: string;
  invoiceNumber: string;
  currency: string;
  currency_id?: string;
  currency_symbol?: string;
  /** Snake_case so review modal labels as "Exchange Rate"; optional FX override */
  exchange_rate?: string;
  /** True when user explicitly saved exchange_rate from this field. */
  exchange_rate_user_set?: boolean;
}

interface BasicOptionsData {
  invoiceNumber: string[];
  currency: string[];
}

interface BasicInfoEditableFieldProps {
  initialData: BasicInfoData;
  optionsData: BasicOptionsData;
  onSave?: (newData: BasicInfoData) => void;
  disabled?: boolean;
  /** When document currency differs from company base currency */
  showExchangeRate?: boolean;
  /** Tally FX docs: show required asterisk and stricter help text */
  exchangeRateRequired?: boolean;
  exchangeRateHelpText?: string;
  exchangeRateError?: string;
  /** Zoho auto-fetch in progress — show loader text in the rate field. */
  exchangeRateLoading?: boolean;
  /** Backend drives display hint (Zoho vs Tally). */
  accountingBackend?: "zoho" | "tally";
  /** FBAC display: invoice_number and currency_symbol deltas (currency column shows code; diff text may show symbol). */
  fieldDiffs?: Partial<
    Record<
      "invoice_number" | "currency_symbol" | "exchange_rate",
      InvoiceBasicFieldDiffSlice
    >
  >;
  /** When true (e.g. review pending), show ~~old~~ new inline; hidden while editing. */
  pendingReviewDiffActive?: boolean;
}

const BasicInfoEditableField: React.FC<BasicInfoEditableFieldProps> = ({
  initialData = {
    invoiceType: "",
    invoiceNumber: "",
    currency: "",
  },
  optionsData = {
    invoiceNumber: [],
    currency: [],
  },
  onSave,
  disabled = false,
  showExchangeRate = false,
  exchangeRateRequired = false,
  exchangeRateHelpText,
  exchangeRateError,
  exchangeRateLoading = false,
  accountingBackend = "zoho",
  fieldDiffs,
  pendingReviewDiffActive = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [data, setData] = useState<BasicInfoData>(initialData);
  const [editData, setEditData] = useState<BasicInfoData>(initialData);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<ChangeItem[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(true);
  const [currenciesError, setCurrenciesError] = useState<string | null>(null);

  useEffect(() => {
    if (isEditing) return;
    setData((prev) => ({
      ...prev,
      invoiceType: initialData.invoiceType,
      invoiceNumber: initialData.invoiceNumber,
      currency: initialData.currency,
      currency_id: initialData.currency_id,
      currency_symbol: initialData.currency_symbol,
      exchange_rate: initialData.exchange_rate ?? "",
      exchange_rate_user_set: initialData.exchange_rate_user_set,
    }));
    setEditData((prev) => ({
      ...prev,
      invoiceType: initialData.invoiceType,
      invoiceNumber: initialData.invoiceNumber,
      currency: initialData.currency,
      currency_id: initialData.currency_id,
      currency_symbol: initialData.currency_symbol,
      exchange_rate: initialData.exchange_rate ?? "",
      exchange_rate_user_set: initialData.exchange_rate_user_set,
    }));
  }, [
    isEditing,
    initialData.invoiceType,
    initialData.invoiceNumber,
    initialData.currency,
    initialData.currency_id,
    initialData.currency_symbol,
    initialData.exchange_rate,
    initialData.exchange_rate_user_set,
  ]);

  // Fetch currencies on component mount
  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        setCurrenciesLoading(true);
        setCurrenciesError(null);
        const response = await fetchCurrencies();
        setCurrencies(response.data);
      } catch (error) {
        console.error("Error fetching currencies:", error);
        setCurrenciesError(handleError(error, "currencies").message);
      } finally {
        setCurrenciesLoading(false);
      }
    };

    loadCurrencies();
  }, []);

  const handleEdit = () => {
    setEditData(data);
    setIsEditing(true);
  };

  // Fields to exclude from the review changes modal (internal IDs, etc.)
  const FIELDS_EXCLUDED_FROM_REVIEW = ["currency_id", "exchange_rate_user_set"];

  // Format field key to display label: snake_case -> "Title Case"
  const formatFieldLabel = (key: string): string =>
    key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");

  const handleSave = () => {
    const changes: ChangeItem[] = [];

    Object.keys(editData).forEach((key) => {
      if (FIELDS_EXCLUDED_FROM_REVIEW.includes(key)) return;

      const fieldKey = key as keyof BasicInfoData;
      const prevVal = data[fieldKey] ?? "";
      const nextVal = editData[fieldKey] ?? "";
      if (prevVal !== nextVal) {
        changes.push({
          field: formatFieldLabel(key),
          previousValue: typeof prevVal === "string" ? prevVal : "",
          newValue: typeof nextVal === "string" ? nextVal : "",
        });
      }
    });

    if (changes.length > 0) {
      setPendingChanges(changes);
      setIsReviewModalOpen(true);
    } else {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditData(data);
    setIsEditing(false);
  };

  const handleConfirmChanges = () => {
    setData(editData);
    setIsEditing(false);
    setIsReviewModalOpen(false);
    setPendingChanges([]);
    onSave?.(editData);
  };

  const handleCancelChanges = () => {
    setIsReviewModalOpen(false);
    setPendingChanges([]);
  };

  const handleFieldChange = (field: keyof BasicInfoData, value: string) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  const findCurrencyByCode = (code: string) => {
    return currencies.find((currency) => currency.currency_code === code);
  };

  const handleCurrencyChange = (currencyId: string) => {
    const selectedCurrency = currencies.find(
      (currency) => currency.currency_id === currencyId,
    );
    if (selectedCurrency) {
      setEditData((prev) => ({
        ...prev,
        currency: selectedCurrency.currency_code,
        currency_id: selectedCurrency.currency_id,
        currency_symbol: selectedCurrency.currency_symbol,
      }));
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  const exchangeRateRaw = (data.exchange_rate ?? "").trim();
  const exchangeRateDisplay = getExchangeRateDisplayText(exchangeRateRaw, {
    showFx: showExchangeRate,
    backend: accountingBackend,
    userSet: data.exchange_rate_user_set,
    loading: exchangeRateLoading,
  });
  const resolvedHelpText =
    exchangeRateHelpText ??
    (exchangeRateLoading
      ? "Fetching the exchange rate for this document date…"
      : exchangeRateRequired
        ? "Required for Tally posting. Enter the rate relative to your company base currency."
        : "Relative to your company base currency. Leave empty to use the latest rate.");

  return (
    <div className="flex w-full flex-col items-start gap-3 border-b border-solid border-neutral-100 pb-4">
      <div className="flex w-full items-center justify-between">
        <span className="text-body-bold font-body-bold text-subtext-color">
          Basic Information
        </span>
        {isEditing ? (
          <div className="flex items-center gap-2">
            <Button variant="brand-primary" size="small" onClick={handleSave}>
              <FeatherSave />
            </Button>
            <Button
              variant="neutral-tertiary"
              size="small"
              onClick={handleCancel}
            >
              <FeatherX />
            </Button>
          </div>
        ) : (
          !disabled && (
            <FeatherEdit
              className="text-body-bold font-body-bold text-subtext-color cursor-pointer hover:text-brand-600 transition-colors"
              onClick={handleEdit}
            />
          )
        )}
      </div>
      <div className="w-full items-start gap-4 grid grid-cols-3">
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
          <span className="text-body font-body text-subtext-color">
            Invoice Type
          </span>
          <Badge>{data.invoiceType}</Badge>
        </div>

        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
          <div className="flex items-center gap-2">
            <span className="text-body font-body text-subtext-color">
              Invoice Number
            </span>
          </div>
          {isEditing ? (
            <div className="flex w-full flex-col gap-1">
              <TextField label="" helpText="">
                <TextField.Input
                  placeholder="INV-2024-001"
                  value={editData.invoiceNumber}
                  onChange={(e) =>
                    handleFieldChange("invoiceNumber", e.target.value)
                  }
                  onKeyDown={handleKeyPress}
                />
              </TextField>
              {(() => {
                const warning = getDocumentNumberValidationWarning(
                  editData.invoiceNumber,
                  data.invoiceType?.toLowerCase().includes("bill")
                    ? "Bill number"
                    : "Invoice number",
                );
                return warning ? (
                  <span className="text-caption font-caption text-warning-700">
                    {warning}
                  </span>
                ) : null;
              })()}
            </div>
          ) : (
            <ReviewPendingInlineValue
              pendingReviewDiffActive={pendingReviewDiffActive}
              isEditing={isEditing}
              oldText={fieldDiffs?.invoice_number?.oldText ?? ""}
              newText={fieldDiffs?.invoice_number?.newText ?? ""}
            >
              <span className="text-body-bold font-body-bold text-default-font">
                {data.invoiceNumber || "Not provided"}
              </span>
            </ReviewPendingInlineValue>
          )}
        </div>
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
          <div className="flex items-center gap-2">
            <span className="text-body font-body text-subtext-color">
              Currency
            </span>
          </div>
          {isEditing ? (
            <div className="flex flex-col gap-1">
              <SearchableDropdown
                items={currencies}
                loading={currenciesLoading}
                trigger={
                  <Button
                    variant="neutral-secondary"
                    className="w-full justify-between h-8"
                    iconRight={<FeatherChevronDown />}
                    disabled={currenciesLoading}
                  >
                    {currenciesLoading
                      ? "Loading currencies..."
                      : editData.currency
                        ? findCurrencyByCode(editData.currency)
                            ?.currency_name_formatted || "Select currency"
                        : "Select currency"}
                  </Button>
                }
                getSearchableText={(currency) =>
                  currency.currency_name_formatted || currency.currency_name
                }
                renderItem={(currency) =>
                  currency.currency_name_formatted || currency.currency_name
                }
                getItemKey={(currency) => currency.currency_id}
                onSelect={(currency) =>
                  handleCurrencyChange(currency.currency_id)
                }
                selectedKey={findCurrencyByCode(editData.currency)?.currency_id}
                searchPlaceholder="Search currencies..."
                emptyMessage="No currencies available"
                noResultsMessage="No currencies match your search"
              />
              {currenciesError && (
                <span className="text-caption font-caption text-error-700">
                  {currenciesError}
                </span>
              )}
            </div>
          ) : data.currency.length < 0 ? (
            <div className="flex items-start rounded-md bg-error-50 px-2 py-1 border-2 border-error-500">
              <span className="text-body font-body text-error-700">
                [Empty - Needs Review]
              </span>
            </div>
          ) : (
            <ReviewPendingInlineValue
              pendingReviewDiffActive={pendingReviewDiffActive}
              isEditing={isEditing}
              oldText={fieldDiffs?.currency_symbol?.oldText ?? ""}
              newText={fieldDiffs?.currency_symbol?.newText ?? ""}
            >
              <Badge variant="neutral">{data.currency}</Badge>
            </ReviewPendingInlineValue>
          )}
        </div>
      </div>

      {showExchangeRate ? (
        <div className="w-full grid grid-cols-3 gap-4">
          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1 max-w-md">
            <span className="text-body font-body text-subtext-color">
              Exchange rate
              {exchangeRateRequired ? (
                <span className="text-error-600"> *</span>
              ) : null}
            </span>
            {isEditing ? (
              <TextField label="" helpText={resolvedHelpText}>
                <TextField.Input
                  type="number"
                  step="any"
                  inputMode="decimal"
                  placeholder="e.g. 83.25"
                  value={editData.exchange_rate ?? ""}
                  onChange={(e) =>
                    handleFieldChange("exchange_rate", e.target.value)
                  }
                  onKeyDown={handleKeyPress}
                  onWheel={(e) => {
                    e.preventDefault();
                  }}
                />
              </TextField>
            ) : (
              <ReviewPendingInlineValue
                pendingReviewDiffActive={pendingReviewDiffActive}
                isEditing={isEditing}
                oldText={fieldDiffs?.exchange_rate?.oldText ?? ""}
                newText={fieldDiffs?.exchange_rate?.newText ?? ""}
              >
                <span
                  className={`text-body-bold font-body-bold ${
                    exchangeRateRaw ||
                    (exchangeRateRequired &&
                      !isImplicitDefaultExchangeRate(exchangeRateRaw))
                      ? "text-default-font"
                      : "text-subtext-color"
                  }`}
                >
                  {exchangeRateDisplay}
                </span>
              </ReviewPendingInlineValue>
            )}
            {exchangeRateError ? (
              <span className="text-caption font-caption text-error-600">
                {exchangeRateError}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      <ReviewChangesModal
        open={isReviewModalOpen}
        onOpenChange={setIsReviewModalOpen}
        changes={pendingChanges}
        onConfirm={handleConfirmChanges}
        onCancel={handleCancelChanges}
        title="Review Basic Information Changes"
        description="These changes may affect amounts and tax implications. Please review carefully before proceeding."
      />
    </div>
  );
};

export default BasicInfoEditableField;
